import { useCallback } from 'react'
import gql from 'graphql-tag'
import type { Asset } from '@ztechapps/common.contract'
import type { UseMutationProps } from '../useMutation'
import { useMutation } from '../useMutation'

/**
 * Supported entity types for asset child resolver mutations.
 * These correspond to the GraphQL mutations: create{EntityType}_assets
 */
export type AssetEntityType =
  | 'Product'
  | 'Order'
  | 'Task'
  | 'Project'
  | 'PurchaseOrder'
  | 'WorkOrder'
  | 'Logistic'
  | 'ReturnOrder'
  | 'HaulerCustomer'
  | 'HaulerDriver'
  | 'MilkPickup'

/**
 * Input for creating an asset on an entity.
 */
export type CreateAssetInput = {
  url: string
  title?: string
  subtitle?: string
  type?: string
}

/**
 * Arguments for the create asset mutation.
 */
export type CreateAssetArgs = {
  filter: { _id: string }
  input: CreateAssetInput
}

/**
 * Result type for useCreateAsset hook
 */
export type UseCreateAssetResult = [
  (args: CreateAssetArgs) => Promise<{ _id: string; assets: Asset[] } | undefined>,
  { error: any; loading: boolean },
]

/**
 * Builds the GraphQL mutation query for creating an asset on a specific entity type.
 * Uses child resolver pattern: create{EntityType}_assets
 */
const buildCreateAssetQuery = (entityType: AssetEntityType) => {
  const mutationName = `create${entityType}_assets`

  const queryString = `
    mutation ${mutationName}($filter: JSON!, $input: JSON!) {
      ${mutationName}(filter: $filter, input: $input) {
        _id
        assets {
          _id
          url
          title
          subtitle
          type
          thumbUrl
          mediumUrl
          largeUrl
        }
      }
    }
  `

  return gql(queryString)
}

/**
 * Hook for creating an asset on an entity using child resolver mutations.
 *
 * Uses the pattern create{EntityType}_assets which:
 * - Triggers the AssetChildRepository.preSave hook for URL sanitization
 * - Provides atomic updates avoiding race conditions
 * - Is more efficient than updating the entire assets array
 *
 * @param entityType - The entity type (Product, Order, Task, Project, PurchaseOrder)
 * @param props - Optional mutation configuration
 * @returns Tuple of [executeFunction, { error, loading }]
 *
 * @example
 * ```tsx
 * const [createAsset, { loading }] = useCreateAsset('Product')
 *
 * await createAsset({
 *   filter: { _id: productId },
 *   input: {
 *     url: 'https://example.com/image.jpg',
 *     title: 'Product Image',
 *     type: AssetTypeEnum.image,
 *   }
 * })
 * ```
 */
export const useCreateAsset = (
  entityType: AssetEntityType,
  { query, ...props } = {} as UseMutationProps
): UseCreateAssetResult => {
  const defaultQuery = buildCreateAssetQuery(entityType)

  const [executeMutation, , { error, loading }] = useMutation<{ _id: string; assets: Asset[] }>({
    query: query ?? defaultQuery,
    ...props,
  })

  // Wrap the mutation to properly extract filter and input from CreateAssetArgs
  const executeCreate = useCallback(
    async (args: CreateAssetArgs) => {
      const result = await executeMutation({
        filter: args.filter,
        input: args.input,
      })
      return result?.data ? (Object.values(result.data)[0] as unknown as { _id: string; assets: Asset[] }) : undefined
    },
    [executeMutation]
  )

  return [executeCreate, { error, loading }]
}
